import React, { useState, useEffect } from 'react'
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  message,
  Tag,
  Space,
  DatePicker,
  Tooltip
} from 'antd'
import { PlusOutlined, BankOutlined, PrinterOutlined } from '@ant-design/icons'
import moment from 'moment'
import { useSelector } from 'react-redux'
import { BillingEnums } from '../../utils/enums'
import {
  getRefunds,
  createRefund,
  approveRefund,
  getPatientBalance
} from '../../backend-integration/api/paymentAllocation'
import RefundForm from './RefundForm'

const RefundManager = () => {
  const authState = useSelector((state) => state.auth)
  const user = authState?.agiliteUser
  const [loading, setLoading] = useState(false)
  const [refunds, setRefunds] = useState([])
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [form] = Form.useForm()
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  })

  useEffect(() => {
    fetchRefunds()
  }, [pagination.current, pagination.pageSize])

  const fetchRefunds = async () => {
    setLoading(true)
    try {
      const { data, total } = await getRefunds({}, pagination.current, pagination.pageSize)
      setRefunds(data)
      setPagination((prev) => ({ ...prev, total }))
    } catch (error) {
      message.error('Failed to fetch refunds')
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = () => {
    form.resetFields()
    setIsModalVisible(true)
  }

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields()
      setLoading(true)

      await createRefund(values)
      message.success('Refund request created successfully')

      setIsModalVisible(false)
      fetchRefunds()
    } catch (error) {
      console.error('Error creating refund:', error)
      message.error('Failed to create refund')
    } finally {
      setLoading(false)
    }
  }

  const handleApprove = async (refundId) => {
    Modal.confirm({
      title: 'Approve Refund',
      content: 'Are you sure you want to approve this refund? This will create a transaction in the patient ledger.',
      onOk: async () => {
        try {
          await approveRefund(refundId)
          message.success('Refund approved successfully')
          fetchRefunds()
        } catch (error) {
          message.error('Failed to approve refund')
          console.error(error)
        }
      }
    })
  }

  const getStatusColor = (status) => {
    const colors = {
      pending_approval: 'warning',
      approved: 'success',
      processed: 'processing',
      cancelled: 'error'
    }
    return colors[status] || 'default'
  }

  const getPaymentMethodIcon = (method) => {
    switch (method) {
      case BillingEnums.paymentMethods.EFT:
        return <BankOutlined />
      default:
        return null
    }
  }

  const columns = [
    {
      title: 'Refund #',
      dataIndex: 'refundNo',
      key: 'refundNo',
      render: (text) => <a>{text}</a>
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (date) => moment(date).format('YYYY-MM-DD')
    },
    {
      title: 'Patient',
      dataIndex: 'patientName',
      key: 'patientName'
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => `R ${amount.toFixed(2)}`,
      align: 'right'
    },
    {
      title: 'Refund Type',
      dataIndex: 'refundType',
      key: 'refundType',
      render: (type) => (
        <Tag color={type === 'patient' ? 'blue' : 'green'}>
          {type === 'patient' ? 'Patient Refund' : 'Medical Aid Refund'}
        </Tag>
      )
    },
    {
      title: 'Payment Method',
      dataIndex: 'paymentMethod',
      key: 'paymentMethod',
      render: (method) => (
        <Space>
          {getPaymentMethodIcon(method)}
          {method}
        </Space>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => <Tag color={getStatusColor(status)}>{status.replace(/_/g, ' ').toUpperCase()}</Tag>
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      key: 'reason',
      ellipsis: {
        showTitle: false
      },
      render: (reason) => (
        <Tooltip placement='topLeft' title={reason}>
          {reason}
        </Tooltip>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          {record.status === 'pending_approval' && (
            <Button type='primary' size='small' onClick={() => handleApprove(record._id)}>
              Approve
            </Button>
          )}
          {record.status === 'approved' && (
            <Button
              icon={<BankOutlined />}
              size='small'
              onClick={() => window.open(`/billing/refund/process/${record._id}`, '_blank')}
            >
              Process
            </Button>
          )}
          <Button
            icon={<PrinterOutlined />}
            size='small'
            onClick={() => window.open(`/billing/refund/print/${record._id}`, '_blank')}
          >
            Print
          </Button>
        </Space>
      )
    }
  ]

  return (
    <Card
      title='Refunds'
      extra={
        <Button type='primary' icon={<PlusOutlined />} onClick={handleCreate}>
          Create Refund
        </Button>
      }
    >
      <Table
        columns={columns}
        dataSource={refunds}
        rowKey='_id'
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} refunds`,
          onChange: (page, pageSize) => {
            setPagination((prev) => ({
              ...prev,
              current: page,
              pageSize
            }))
          }
        }}
      />

      <Modal
        title='Create Refund'
        visible={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => setIsModalVisible(false)}
        width={800}
        confirmLoading={loading}
      >
        <RefundForm form={form} />
      </Modal>
    </Card>
  )
}

export default RefundManager
